import {useTranslation} from "react-i18next";
import * as Yup from "yup";
import {useSetRecoilState} from "recoil";
import * as Renders from "../../../component/AdvancedFiltersRenders";
import AdvancedFilter from "../../../component/AdvancedFilter";
import {STATE_ACTIVE_FILTERS} from "../constants/CardRecoil";
import {
    CARD_STATUSES,
    cardDefaultValues,
    ID_FILTER_CUSTOMER, ID_FILTER_DEALERS,
    ID_FILTER_EXPIRATION_DATE_FROM,
    ID_FILTER_EXPIRATION_DATE_TO, ID_FILTER_LEGAL_ENTITIES,
    ID_FILTER_NETWORK_CODE,
    ID_FILTER_PAN,
    ID_FILTER_PAN_ALIAS,
    ID_FILTER_PAN_ALIAS_CUSTOMER,
    ID_FILTER_STATUS,
    NETWORK_CODE
} from "../constants/CardConstants";
import {addFilter, renderMultiSelect} from "../../../utils/FilterUtils";
import {useContext, useState} from "react";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

function CardFilters() {
    const { t } = useTranslation("finance", { keyPrefix: "payments.amex.cards" });
    const { t: tg } = useTranslation("translation");

    const {
        globalData: { user },
    } = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const setFilters = useSetRecoilState(STATE_ACTIVE_FILTERS);
    const [dealers, setDealers] = useState<any[]>(dealerOptions);
    const schema = Yup.object().shape({
        [ID_FILTER_EXPIRATION_DATE_FROM]: Yup.date()
            .nullable()
            .test(
                "testFrom",
                t("filters.errors.invalid-expiration-date-from"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.expirationDateTo ||
                        value <= context.parent.expirationDateTo
                    );
                }
            )
            .test(
                "date-range",
                t("filters.errors.invalid-expiration-date-from"),
                function (value) {
                    const { dateTo } = this.parent;
                    return !value || !dateTo || value <= dateTo;
                }
            )
            .typeError(t("filters.errors.invalid-expiration-date-from")),
        [ID_FILTER_EXPIRATION_DATE_TO]: Yup.date()
            .nullable()
            .test(
                "testTo",
                t("filters.errors.invalid-expiration-date-to"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.expirationDateFrom ||
                        value >= context.parent.expirationDateFrom
                    );
                }
            )
            .test(
                "date-range",
                t("filters.errors.invalid-expiration-date-to"),
                function (value) {
                    const { dateFrom } = this.parent;
                    return !value || !dateFrom || value >= dateFrom;
                }
            )
            .typeError(t("filters.errors.invalid-expiration-date-to")),
        dealers: Yup.array(),
        legalEntities: Yup.array().min(1, tg('validation.selectAtLeastOne'))
    });

    const defaultValues = {
        [ID_FILTER_CUSTOMER]: "",
        [ID_FILTER_NETWORK_CODE]: [],
        [ID_FILTER_STATUS]: [],
        [ID_FILTER_EXPIRATION_DATE_FROM]: null,
        [ID_FILTER_EXPIRATION_DATE_TO]: null,
        [ID_FILTER_PAN]: "",
        [ID_FILTER_PAN_ALIAS]: "",
        [ID_FILTER_PAN_ALIAS_CUSTOMER]: "",
        legalEntities: legalEntityOptions.map((x: any) => x.value),
        dealers: [],
    };

    const fields = {
        main: {
            [ID_FILTER_CUSTOMER]: Renders.renderTextField(
                ID_FILTER_CUSTOMER,
                t("filters." + ID_FILTER_CUSTOMER)
            ),
            [ID_FILTER_NETWORK_CODE]: Renders.renderMultiSelect(
                ID_FILTER_NETWORK_CODE,
                t("filters." + ID_FILTER_NETWORK_CODE),
                NETWORK_CODE.map((x) => {
                    return { ...x, label: t(x.label) };
                })
            ),
            [ID_FILTER_STATUS]: Renders.renderMultiSelect(
                ID_FILTER_STATUS,
                t("filters." + ID_FILTER_STATUS),
                CARD_STATUSES.map((x) => {
                    return { ...x, label: t(x.label) };
                })
            ),
        },
        adv: {
            [ID_FILTER_EXPIRATION_DATE_FROM]: Renders.renderDatePickerFrom(
                ID_FILTER_EXPIRATION_DATE_FROM,
                t("filters." + ID_FILTER_EXPIRATION_DATE_FROM)
            ),
            [ID_FILTER_EXPIRATION_DATE_TO]: Renders.rendereDatePickerTo(
                ID_FILTER_EXPIRATION_DATE_TO,
                t("filters." + ID_FILTER_EXPIRATION_DATE_TO)
            ),
            [ID_FILTER_PAN]: Renders.renderTextField(
                ID_FILTER_PAN,
                t("filters." + ID_FILTER_PAN)
            ),
            [ID_FILTER_PAN_ALIAS]: Renders.renderTextField(
                ID_FILTER_PAN_ALIAS,
                t("filters." + ID_FILTER_PAN_ALIAS)
            ),
            [ID_FILTER_PAN_ALIAS_CUSTOMER]: Renders.renderTextField(
                ID_FILTER_PAN_ALIAS_CUSTOMER,
                t("filters." + ID_FILTER_PAN_ALIAS_CUSTOMER)
            ),
            legalEntities: renderMultiSelect(
                tg("shared.legalEntities"),
                legalEntityOptions,
                "legalEntities",
                legalEntityOptions.length <= 1,
                (value: any, setValue: any) => {
                    setValue('dealers', []);
                    let dealers = user?.dealers
                        ?.filter(
                            (x: any) =>
                                value?.length > 0 &&
                                x.legalEntities.some((e: any) => value.includes(e.name)) &&
                                x.name.toLowerCase() !== "all"
                        )
                        .map((x: any) => {
                            return {
                                label: tg(`dealers.${x.name}`),
                                value: x.name,
                            };
                        });
                    setDealers(dealers);
                }
            ),
            dealers: renderMultiSelect(tg("shared.dealers"), dealers, "dealers", dealerOptions.length <= 1),
        },
    };

    const submit = (data: any) => {
        Object.keys(defaultValues).forEach((param) => {
            addFilter(setFilters, param, data[param]);
        });
        addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
        addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);
    };

    const reset = () => {
        setFilters([]);
    };

    return (
        <AdvancedFilter
            schema={schema}
            mainFields={fields.main}
            fields={fields.adv}
            defaultValues={defaultValues}
            onSubmit={submit}
            onReset={reset}
            customDefaultValues={cardDefaultValues}

        />
    );
}

export default CardFilters;
